import React, { useState, useMemo } from 'react';
import './GlobalVariablesPanel.css';

const GlobalVariablesPanel = ({ variables, onUpdate, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [editingId, setEditingId] = useState(null);

    // Get unique categories
    const categories = useMemo(() => {
        const cats = new Set(variables.variables.map(v => v.category));
        return ['all', ...Array.from(cats)];
    }, [variables]);

    // Filter variables
    const filteredVariables = useMemo(() => {
        return variables.variables.filter(v => {
            const matchesSearch = v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                v.description.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'all' || v.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });
    }, [variables, searchTerm, selectedCategory]);

    const addVariable = () => {
        const newVar = {
            id: `var-${Date.now()}`,
            name: `--new-variable-${variables.variables.length + 1}`,
            type: 'color',
            value: '#000000',
            description: 'New variable',
            category: 'custom'
        };
        onUpdate({ ...variables, variables: [...variables.variables, newVar] });
        setEditingId(newVar.id);
    };

    const updateVariable = (id, updates) => {
        const updated = variables.variables.map(v =>
            v.id === id ? { ...v, ...updates } : v
        );
        onUpdate({ ...variables, variables: updated });
    };

    const deleteVariable = (id) => {
        if (window.confirm('Are you sure you want to delete this variable?')) {
            const updated = variables.variables.filter(v => v.id !== id);
            onUpdate({ ...variables, variables: updated });
        }
    };

    const renderVariableInput = (variable) => {
        const isEditing = editingId === variable.id;

        return (
            <div key={variable.id} className="variable-row">
                <div className="variable-info">
                    <div className="variable-name-section">
                        {isEditing ? (
                            <input
                                type="text"
                                className="variable-name-input"
                                value={variable.name}
                                onChange={(e) => updateVariable(variable.id, { name: e.target.value })}
                                placeholder="--variable-name"
                            />
                        ) : (
                            <code className="variable-name">{variable.name}</code>
                        )}
                        <span className="variable-category-badge">{variable.category}</span>
                    </div>

                    {isEditing ? (
                        <input
                            type="text"
                            className="variable-description-input"
                            value={variable.description}
                            onChange={(e) => updateVariable(variable.id, { description: e.target.value })}
                            placeholder="Description"
                        />
                    ) : (
                        <span className="variable-description">{variable.description}</span>
                    )}
                </div>

                <div className="variable-value-section">
                    {variable.type === 'color' && (
                        <div className="color-input-group">
                            <input
                                type="color"
                                value={variable.value}
                                onChange={(e) => updateVariable(variable.id, { value: e.target.value })}
                                className="color-picker"
                            />
                            <input
                                type="text"
                                value={variable.value}
                                onChange={(e) => updateVariable(variable.id, { value: e.target.value })}
                                className="color-text-input"
                                placeholder="#000000"
                            />
                        </div>
                    )}

                    {variable.type === 'font' && (
                        <input
                            type="text"
                            value={variable.value}
                            onChange={(e) => updateVariable(variable.id, { value: e.target.value })}
                            className="font-input"
                            placeholder="Arial, sans-serif"
                        />
                    )}

                    {variable.type === 'size' && (
                        <input
                            type="text"
                            value={variable.value}
                            onChange={(e) => updateVariable(variable.id, { value: e.target.value })}
                            className="size-input"
                            placeholder="16px"
                        />
                    )}

                    {(variable.type === 'text' || variable.type === 'number') && (
                        <input
                            type={variable.type === 'number' ? 'number' : 'text'}
                            value={variable.value}
                            onChange={(e) => updateVariable(variable.id, { value: e.target.value })}
                            className="text-input"
                        />
                    )}
                </div>

                <div className="variable-actions">
                    {isEditing ? (
                        <button
                            className="btn-done"
                            onClick={() => setEditingId(null)}
                            title="Done editing"
                        >
                            ✓
                        </button>
                    ) : (
                        <button
                            className="btn-edit"
                            onClick={() => setEditingId(variable.id)}
                            title="Edit variable"
                        >
                            ✏️
                        </button>
                    )}
                    <button
                        className="btn-delete"
                        onClick={() => deleteVariable(variable.id)}
                        title="Delete variable"
                    >
                        🗑️
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="global-variables-overlay" onClick={onClose}>
            <div className="global-variables-panel" onClick={(e) => e.stopPropagation()}>
                <div className="panel-header">
                    <h2>Global Variables</h2>
                    <button className="btn-close" onClick={onClose}>×</button>
                </div>

                <div className="panel-toolbar">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search variables..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />

                    <select
                        className="category-filter"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        {categories.map(cat => (
                            <option key={cat} value={cat}>
                                {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </option>
                        ))}
                    </select>

                    <button className="btn-add-variable" onClick={addVariable}>
                        + Add Variable
                    </button>
                </div>

                <div className="variables-list">
                    {filteredVariables.length === 0 ? (
                        <div className="empty-state">
                            <p>No variables found</p>
                            {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
                        </div>
                    ) : (
                        filteredVariables.map(renderVariableInput)
                    )}
                </div>

                <div className="panel-footer">
                    <div className="footer-info">
                        <span>{filteredVariables.length} of {variables.variables.length} variables</span>
                    </div>
                    <button className="btn-primary" onClick={onClose}>
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GlobalVariablesPanel;
